import React, { useMemo } from "react";
import { uploadQr } from "@rflocal/shared";
import QRCode from "@scripts/react/components/QRCode";
import { useCurrentQrSession, useQrUploadLoading, useQrUploadError } from "@rapid/store";
import { twClassNames } from "@scripts/react/helpers/twClassNames";

interface QrUploadWidgetProps {
    size?: number;
    className?: string;
}

enum QrDisplayState {
    LOADING = "loading",
    ERROR = "error",
    SUCCESS = "success",
    DEFAULT = "default"
}

export const QrUploadWidget: React.FC<QrUploadWidgetProps> = ({ size = 90, className = "" }) => {
    const currentQrSession = useCurrentQrSession();
    const qrLoading = useQrUploadLoading();
    const qrError = useQrUploadError();

    // Generate QR upload URL
    const qrUploadUrl = useMemo(() => {
        if (!currentQrSession?.id) return null;
        return uploadQr.qrUploadUrl(currentQrSession.id);
    }, [currentQrSession?.id]);

    const getQRDisplayState = () => {
        if (qrLoading) return QrDisplayState.LOADING;
        if (qrError) return QrDisplayState.ERROR;
        if (qrUploadUrl) return QrDisplayState.SUCCESS;
        return QrDisplayState.DEFAULT;
    };

    const displayState = getQRDisplayState();

    switch (displayState) {
        case QrDisplayState.LOADING:
            return (
                <div
                    className={twClassNames(
                        "flex animate-pulse items-center justify-center rounded-lg bg-gray-200 text-tertiary transition duration-200 group-hover:bg-purboflow-200 group-hover:text-brand",
                        `h-[${size}px]`,
                        `w-[${size}px]`,
                        className
                    )}
                >
                    <i className="far fa-spinner fa-spin" />
                </div>
            );
        case QrDisplayState.ERROR:
            return (
                <div
                    className={twClassNames(
                        "flex items-center justify-center rounded-lg border border-red-200 bg-red-50 text-xs text-red-600 transition duration-200",
                        `h-[${size}px]`,
                        `w-[${size}px]`,
                        className
                    )}
                >
                    QR Error
                </div>
            );
        case QrDisplayState.SUCCESS:
            return <QRCode data={qrUploadUrl} size={size} className={className} />;
        default:
            return (
                <div
                    className={twClassNames(
                        "flex animate-pulse items-center justify-center rounded-lg bg-gray-200 text-tertiary transition duration-200 group-hover:bg-purboflow-200 group-hover:text-brand",
                        `h-[${size}px]`,
                        `w-[${size}px]`,
                        className
                    )}
                >
                    <i className="far fa-spinner fa-spin" />
                </div>
            );
    }
};
